import type { Money } from '@/types/schemas';

/**
 * Money arrives as a 2-decimal string ("1500.00"). Arithmetic happens in integer paise so a
 * total can never pick up floating-point noise. Real totals come from the server — the client
 * only computes previews and per-month figures for display.
 */
export function toPaise(value: string): number {
  const [rupees = '0', fraction = ''] = value.trim().split('.');
  return Number(rupees) * 100 + Number(fraction.padEnd(2, '0').slice(0, 2));
}

export function fromPaise(paise: number): Money {
  const sign = paise < 0 ? '-' : '';
  const absolute = Math.abs(Math.round(paise));
  const rupees = Math.floor(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, '0');
  return `${sign}${rupees}.${fraction}`;
}

const wholeRupees = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const withPaise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "1500.00" → "₹1,500"; "1500.50" → "₹1,500.50". Display only. */
export function formatMoney(value: string): string {
  const paise = toPaise(value);
  return paise % 100 === 0 ? wholeRupees.format(paise / 100) : withPaise.format(paise / 100);
}

/** An amount split evenly, e.g. the per-month price of a plan. Display only. */
export function formatMoneyShare(value: string, parts: number): string {
  if (parts <= 0) return formatMoney(value);
  return formatMoney(fromPaise(Math.round(toPaise(value) / parts)));
}
